import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bell, BellRing, GraduationCap, School, Users, Calendar, Activity, BookOpen, Award, Library, Trophy, Plane, Zap } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, testPush, pushDiagnostics, pushVerify } from "@/lib/push";
import { StudyRemindersSection } from "@/components/StudyRemindersSection";
import { FamilySection } from "@/components/settings/FamilySection";
import { JarvisBrainCard } from "@/components/settings/JarvisBrainCard";
import { NotificationsSection } from "@/components/settings/NotificationsSection";
import { SchoolAccountsSection } from "@/components/settings/SchoolAccountsSection";
import { CalendarsSection } from "@/components/settings/CalendarsSection";
import { FootballTeamsSection } from "@/components/settings/FootballTeamsSection";
import { FlightsSection } from "@/components/settings/FlightsSection";
import { SettingsDialogs } from "@/components/settings/SettingsDialogs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = ["#E07A5F", "#97BC62", "#F6CA83", "#82A0BC", "#3D5A80", "#C8B6E2", "#E8A0BF"];
const emptyForm = { name: "", url: "", color: COLORS[0], owner: "" };

export default function SettingsPage() {
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushVerified, setPushVerified] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);

  // Google OAuth
  const [googleCfg, setGoogleCfg] = useState(null); // {configured, client_id, client_secret_set}
  const [googleStatus, setGoogleStatus] = useState(null);
  const [googleForm, setGoogleForm] = useState({ client_id: "", client_secret: "" });
  const [gmailLabels, setGmailLabels] = useState([]);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Scheduler
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [briefingPreview, setBriefingPreview] = useState(null);

  // Push diagnostik
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagSteps, setDiagSteps] = useState(null);

  // Settings-meny: null = visa meny, annars vald sektion
  const [activeSection, setActiveSection] = useState(null);

  // Ny familjemedlem-dialog
  const [newMemberOpen, setNewMemberOpen] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "", email: "", is_child: false, avatar_color: COLORS[0], extra_emails: "", phone: "",
  });
  const [creatingMember, setCreatingMember] = useState(false);

  const createNewMember = async () => {
    if (!newMember.name.trim() || !newMember.email.trim()) {
      toast.error("Namn och e-post krävs");
      return;
    }
    setCreatingMember(true);
    try {
      const payload = {
        name: newMember.name.trim(),
        email: newMember.email.trim().toLowerCase(),
        is_child: newMember.is_child,
        avatar_color: newMember.avatar_color,
        extra_emails: newMember.extra_emails
          .split(",").map((s) => s.trim()).filter(Boolean),
        phone: newMember.phone.trim() || null,
      };
      const { data } = await api.post("/users", payload);
      toast.success(`${data.name} tillagd`);
      setNewMemberOpen(false);
      setNewMember({ name: "", email: "", is_child: false, avatar_color: COLORS[0], extra_emails: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa medlem");
    } finally {
      setCreatingMember(false);
    }
  };

  const load = async () => {
    const [f, u] = await Promise.all([api.get("/feeds"), api.get("/users")]);
    setFeeds(f.data);
    setUsers(u.data);
    try {
      const s = await getPushStatus();
      setPushStatus(s);
      setPushVerified(await pushVerify());
    } catch (e) {
      setPushStatus({ supported: false, permission: "denied", vapid_configured: false, firebase_configured: false });
    }
    try {
      const [c, gs] = await Promise.all([api.get("/google/config"), api.get("/google/status")]);
      setGoogleCfg(c.data);
      if (c.data.configured) setGoogleForm((f) => ({ ...f, client_id: c.data.client_id }));
      setGoogleStatus(gs.data);
      if (gs.data.connected) {
        try {
          const lbls = await api.get("/google/gmail/labels");
          setGmailLabels(lbls.data.labels || []);
        } catch (_) {}
      }
    } catch (_) {}
    try {
      const { data } = await api.get("/scheduler/status");
      setSchedulerStatus(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, color: COLORS[feeds.length % COLORS.length] });
    setOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({ name: f.name, url: f.url, color: f.color, owner: f.owner || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error("Namn och URL krävs");
      return;
    }
    try {
      if (editing) await api.put(`/feeds/${editing.id}`, form);
      else await api.post("/feeds", form);
      toast.success(editing ? "Uppdaterad - hämtar händelser..." : "Tillagd - hämtar händelser...");
      setOpen(false);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error("Kunde inte spara");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort denna kalender?")) return;
    await api.delete(`/feeds/${id}`);
    toast.success("Borttagen");
    load();
  };

  const refresh = async (id) => {
    toast.loading("Uppdaterar...", { id: "refresh" });
    const { data } = await api.post(`/feeds/${id}/refresh`);
    if (data.ok) toast.success(`Hämtade ${data.count} händelser`, { id: "refresh" });
    else toast.error(`Fel: ${data.error?.slice(0, 60)}`, { id: "refresh" });
    load();
  };

  // ---- Värmdö Classroom (SAML-scraper) ----
  const [varmdoCreds, setVarmdoCreds] = useState([]);
  const [varmdoOpen, setVarmdoOpen] = useState(false);
  const [varmdoForm, setVarmdoForm] = useState({ email: "", username: "", password: "", label: "" });
  const [varmdoBusy, setVarmdoBusy] = useState(false);
  const [editingVarmdo, setEditingVarmdo] = useState(null); // null = create, doc = edit
  const [expandedCredId, setExpandedCredId] = useState(null);
  const [credCourses, setCredCourses] = useState({}); // {credId: [courses]}
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [courseItems, setCourseItems] = useState({}); // {courseId: {stream, coursework}}
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const toggleExpandCred = async (credId) => {
    if (expandedCredId === credId) {
      setExpandedCredId(null);
      return;
    }
    setExpandedCredId(credId);
    if (!credCourses[credId]) {
      try {
        const { data } = await api.get(`/varmdo/classroom/credentials/${credId}/courses`);
        setCredCourses((s) => ({ ...s, [credId]: data.items || [] }));
      } catch (e) {
        toast.error("Kunde inte ladda kurser");
      }
    }
  };

  const toggleExpandCourse = async (credId, courseId) => {
    const key = `${credId}::${courseId}`;
    if (expandedCourseId === key) {
      setExpandedCourseId(null);
      return;
    }
    setExpandedCourseId(key);
    if (!courseItems[key]) {
      try {
        const { data } = await api.get(`/varmdo/classroom/credentials/${credId}/courses/${courseId}/items`);
        setCourseItems((s) => ({ ...s, [key]: data }));
      } catch (e) {
        toast.error("Kunde inte ladda kursinnehåll");
      }
    }
  };

  const loadVarmdo = async () => {
    try {
      const { data } = await api.get("/varmdo/classroom/credentials");
      setVarmdoCreds(data.items || []);
    } catch (_) { setVarmdoCreds([]); }
  };

  const connectVarmdo = async () => {
    // EDIT mode (existing cred): use PATCH; if password blank, only update metadata
    if (editingVarmdo) {
      setVarmdoBusy(true);
      try {
        const body = {
          label: varmdoForm.label,
          email: varmdoForm.email,
          username: varmdoForm.username,
        };
        if (varmdoForm.password) body.password = varmdoForm.password;
        await api.patch(`/varmdo/classroom/credentials/${editingVarmdo.id}`, body);
        toast.success("Sparat");
        if (varmdoForm.password) {
          // Direkt försök synka med nytt lösenord
          try {
            const { data } = await api.post(`/varmdo/classroom/credentials/${editingVarmdo.id}/sync`);
            toast.success(`Inloggning OK — ${data.courses} kurser hämtade`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Sparat men sync misslyckades");
          }
        }
        setVarmdoOpen(false);
        setEditingVarmdo(null);
        setVarmdoForm({ email: "", username: "", password: "", label: "" });
        await loadVarmdo();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Kunde inte spara");
      } finally {
        setVarmdoBusy(false);
      }
      return;
    }
    // CREATE mode
    if (!varmdoForm.email || !varmdoForm.username || !varmdoForm.password) {
      toast.error("Fyll i skol-e-post, portal-användarnamn och lösenord");
      return;
    }
    setVarmdoBusy(true);
    try {
      const { data } = await api.post("/varmdo/classroom/connect", varmdoForm);
      toast.success(`${data.label}: ${data.courses} kurser, ${data.coursework} uppgifter hämtade`);
      setVarmdoOpen(false);
      setVarmdoForm({ email: "", username: "", password: "", label: "" });
      await loadVarmdo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anslutning misslyckades");
    } finally {
      setVarmdoBusy(false);
    }
  };

  const openEditVarmdo = (cred) => {
    setEditingVarmdo(cred);
    setVarmdoForm({
      label: cred.label || "",
      email: cred.email || "",
      username: cred.username || "",
      password: "",
    });
    setVarmdoOpen(true);
  };

  const openCreateVarmdo = () => {
    setEditingVarmdo(null);
    setVarmdoForm({ email: "", username: "", password: "", label: "" });
    setVarmdoOpen(true);
  };

  const syncVarmdo = async (credId, deep = false) => {
    setVarmdoBusy(true);
    try {
      const url = deep
        ? `/varmdo/classroom/credentials/${credId}/sync?deep=true`
        : `/varmdo/classroom/credentials/${credId}/sync`;
      const { data } = await api.post(url);
      if (data.queued) {
        toast.success(deep ? "Djup-sync startad (~75s i bakgrunden)" : "Sync startad (~15s i bakgrunden)");
      } else {
        toast.info(data.message || "Sync redan igång");
      }
      // Polla status var 3:e sek tills 'syncing' försvinner (max 120s)
      const startedAt = Date.now();
      const poll = async () => {
        const { data: d } = await api.get("/varmdo/classroom/credentials");
        setVarmdoCreds(d.items || []);
        const me = (d.items || []).find((c) => c.id === credId);
        if (me?.last_status === "syncing" && Date.now() - startedAt < 120000) {
          setTimeout(poll, 3000);
        } else if (me?.last_status === "ok") {
          toast.success(`${me.label}: ${me.enrolled_courses ?? "?"} kurser, ${me.last_courses_count ?? "?"} med uppgifter`);
          setVarmdoBusy(false);
        } else if (me?.last_status === "error") {
          toast.error(me.last_error || "Sync misslyckades");
          setVarmdoBusy(false);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync misslyckades");
      setVarmdoBusy(false);
    }
  };

  const removeVarmdo = async (credId) => {
    if (!window.confirm("Ta bort detta Värmdö-konto?")) return;
    await api.delete(`/varmdo/classroom/credentials/${credId}`);
    toast.success("Borttaget");
    await loadVarmdo();
  };

  const syncVarmdoGmail = async (credId) => {
    setVarmdoBusy(true);
    try {
      const { data } = await api.post(`/varmdo/classroom/credentials/${credId}/sync-gmail?days=14`);
      if (data.ok) {
        toast.success(`Mejl synkat (${data.count} st sparade)`);
      } else {
        toast.error(data.error || "Gmail-sync misslyckades");
      }
      await loadVarmdo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gmail-sync misslyckades");
    } finally {
      setVarmdoBusy(false);
    }
  };

  const toggleChildRole = async (u) => {
    const becomesChild = !u.is_child;
    const defaultEmail = u.linked_classroom_emails?.[0] || u.email;
    if (becomesChild && !window.confirm(`Markera ${u.name} som barn? De ser då bara data kopplat till ${defaultEmail}.`)) return;
    try {
      await api.put(`/users/${u.id}/family-role`, {
        is_child: becomesChild,
        linked_classroom_emails: becomesChild ? [defaultEmail] : [],
      });
      toast.success(becomesChild ? `${u.name} är nu markerad som barn` : `${u.name} ser nu hela familjens data`);
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte uppdatera rollen");
    }
  };

  const editUserPhone = async (u) => {
    const current = u.phone || "";
    const next = window.prompt(
      `Telefonnummer för ${u.name} (svenskt mobilnummer, t.ex. 070-123 45 67). Lämna tomt för att radera:`,
      current,
    );
    if (next === null) return; // användaren avbröt
    try {
      await api.put(`/users/${u.id}/phone`, { phone: next.trim() || null });
      toast.success(next.trim() ? `Numret sparat för ${u.name}` : `Numret raderat för ${u.name}`);
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara numret");
    }
  };

  const editUserExtraEmails = async (u) => {
    const current = (u.extra_emails || []).join(", ");
    const next = window.prompt(
      `Extra e-postadresser för ${u.name} (kommaseparerade). Alla får PIN-koder & briefing.\n\nPrimär: ${u.email}\n`,
      current,
    );
    if (next === null) return;
    const list = next
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    try {
      await api.put(`/users/${u.id}/extra-emails`, { extra_emails: list });
      toast.success(
        list.length > 0
          ? `${list.length} extra adress(er) sparad${list.length > 1 ? "e" : ""} för ${u.name}`
          : `Extra adresser raderade för ${u.name}`,
      );
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara adresserna");
    }
  };

  useEffect(() => {
    loadVarmdo();
  }, []);

  // ---- Vklass parent (familj-login) ----
  const [vklassOpen, setVklassOpen] = useState(false);
  const [vklassForm, setVklassForm] = useState({ username: "", password: "", label: "" });
  const [vklassBusy, setVklassBusy] = useState(false);
  const [vklassAccounts, setVklassAccounts] = useState([]);
  const [editingVklass, setEditingVklass] = useState(null); // null = create | parent doc | student doc

  const loadVklassAccounts = async () => {
    try {
      const { data } = await api.get("/vklass/accounts");
      setVklassAccounts(data.items || []);
    } catch (_) { setVklassAccounts([]); }
  };

  useEffect(() => { loadVklassAccounts(); }, []);

  const connectVklass = async () => {
    // Edit-mode för förälder eller elev
    if (editingVklass) {
      setVklassBusy(true);
      try {
        const body = {};
        if (vklassForm.label !== undefined) body.label = vklassForm.label;
        if (vklassForm.username !== undefined) body.username = vklassForm.username;
        if (vklassForm.password) body.password = vklassForm.password;
        const url = editingVklass.kind === "parent"
          ? `/vklass/parent-credentials/${editingVklass.id}`
          : `/vklass/student-credentials/${editingVklass.id}`;
        await api.patch(url, body);
        toast.success("Sparat");
        if (vklassForm.password) {
          // Synka direkt med nytt lösenord
          try {
            if (editingVklass.kind === "parent") {
              const { data } = await api.post(`/vklass/parent-credentials/${editingVklass.id}/sync`);
              toast.success(`Inloggning OK — ${data.children} barn, ${data.new_feeds} nya feeds`);
            } else {
              const { data } = await api.post(`/vklass/feeds/${editingVklass.feed_id}/sync-assignments`);
              toast.success(`Inloggning OK — ${data.count} uppgifter`);
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Sparat men sync misslyckades");
          }
        }
        setVklassOpen(false);
        setEditingVklass(null);
        setVklassForm({ username: "", password: "", label: "" });
        await loadVklassAccounts();
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Kunde inte spara");
      } finally {
        setVklassBusy(false);
      }
      return;
    }
    // CREATE: ny förälder-anslutning (skapar feeds automatiskt)
    if (!vklassForm.username || !vklassForm.password) {
      toast.error("Fyll i användarnamn och lösenord");
      return;
    }
    setVklassBusy(true);
    try {
      const { data } = await api.post("/vklass/connect", vklassForm);
      const added = (data?.created || []).length;
      toast.success(added ? `Vklass anslutet — ${added} barn/feeds tillagda` : "Vklass anslutet");
      setVklassOpen(false);
      setVklassForm({ username: "", password: "", label: "" });
      await load();
      await loadVklassAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ansluta till Vklass");
    } finally {
      setVklassBusy(false);
    }
  };

  const openCreateVklass = () => {
    setEditingVklass(null);
    setVklassForm({ username: "", password: "", label: "" });
    setVklassOpen(true);
  };

  const openEditVklass = (acc) => {
    setEditingVklass(acc);
    setVklassForm({ label: acc.label || "", username: acc.username || "", password: "" });
    setVklassOpen(true);
  };

  const syncVklassAccount = async (acc) => {
    setVklassBusy(true);
    try {
      if (acc.kind === "parent") {
        const { data } = await api.post(`/vklass/parent-credentials/${acc.id}/sync`);
        toast.success(`Synk OK — ${data.children} barn, ${data.new_feeds} nya feeds`);
      } else {
        const { data } = await api.post(`/vklass/feeds/${acc.feed_id}/sync-assignments`);
        toast.success(data.ok ? `Synk OK — ${data.count} uppgifter` : `Fel: ${data.error}`);
      }
      await loadVklassAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync misslyckades");
    } finally {
      setVklassBusy(false);
    }
  };

  const removeVklassAccount = async (acc) => {
    if (!window.confirm(`Ta bort ${acc.kind === "parent" ? "förälder" : "elev"}-konto för ${acc.label}?`)) return;
    if (acc.kind === "parent") {
      await api.delete(`/vklass/parent-credentials/${acc.id}`);
    } else {
      await api.delete(`/vklass/feeds/${acc.feed_id}/student-credentials`);
    }
    toast.success("Borttaget");
    await loadVklassAccounts();
  };

  // ---- Vklass student credentials (per child, från feed-kort längre ner) ----
  const [studentOpen, setStudentOpen] = useState(false);
  const [studentForm, setStudentForm] = useState({ feed_id: null, owner: "", username: "", password: "" });
  const [studentBusy, setStudentBusy] = useState(false);
  const [students, setStudents] = useState([]);

  const loadStudents = async () => {
    try {
      const { data } = await api.get("/vklass/students");
      setStudents(data.items || []);
    } catch (_) {}
  };
  useEffect(() => { loadStudents(); }, []);

  const openStudentDialog = (feed) => {
    setStudentForm({ feed_id: feed.id, owner: feed.owner || feed.name, username: "", password: "" });
    setStudentOpen(true);
  };

  const saveStudentCredentials = async () => {
    if (!studentForm.username || !studentForm.password) {
      toast.error("Användarnamn och lösenord krävs");
      return;
    }
    setStudentBusy(true);
    try {
      const { data } = await api.post(`/vklass/feeds/${studentForm.feed_id}/student-credentials`, {
        username: studentForm.username,
        password: studentForm.password,
      });
      toast.success(`Elev-inlogg sparad — ${data.count} uppgifter hämtade`);
      setStudentOpen(false);
      setStudentForm({ feed_id: null, owner: "", username: "", password: "" });
      loadStudents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inloggning misslyckades");
    } finally {
      setStudentBusy(false);
    }
  };

  const removeStudentCredentials = async (feedId) => {
    if (!window.confirm("Ta bort elev-inlogg?")) return;
    await api.delete(`/vklass/feeds/${feedId}/student-credentials`);
    toast.success("Elev-inlogg borttagen");
    loadStudents();
  };

  const togglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) {
        await enablePush();
        toast.success("Notiser påslagna");
      } else {
        await disablePush();
        toast.success("Notiser avstängda");
      }
      const s = await getPushStatus();
      setPushStatus(s);
      setPushVerified(await pushVerify());
    } catch (e) {
      toast.error(e.message || "Något gick fel");
    } finally {
      setPushBusy(false);
    }
  };

  const runPushDiagnostics = async () => {
    setDiagBusy(true);
    setDiagSteps(null);
    try {
      const steps = await pushDiagnostics();
      setDiagSteps(steps);
      const failed = steps.filter((s) => s.status === "fail");
      if (failed.length === 0) toast.success("Diagnostik klar — allt ser bra ut!");
      else toast.error(`${failed.length} problem hittades — se listan`);
    } catch (e) {
      toast.error("Diagnostik kraschade: " + (e?.message || e));
    } finally {
      setDiagBusy(false);
    }
  };


  const runTestPush = async () => {
    try {
      const r = await testPush();
      if (r.ok) toast.success(`Testnotis skickad till ${r.sent} enhet(er)`);
      else toast.error("Ingen enhet att skicka till");
    } catch (e) {
      toast.error("Kunde inte skicka test");
    }
  };

  const reRegisterPush = async () => {
    setPushBusy(true);
    try {
      try { await disablePush(); } catch (_) { /* fortsätt även om disable failar */ }
      await enablePush();
      const s = await getPushStatus();
      setPushStatus(s);
      setPushVerified(await pushVerify());
      toast.success("Enhet om-registrerad — testa skicka en push");
    } catch (e) {
      toast.error("Kunde inte registrera om: " + (e?.message || "okänt fel"));
    } finally {
      setPushBusy(false);
    }
  };

  // Backend är sanningskällan: token verifieras (och självläks från FCM om
  // localStorage rensats) via pushVerify — localStorage ensam räcker inte.
  const pushEnabled = pushStatus?.permission === "granted"
    && pushVerified?.registered === true
    && pushVerified?.enabled === true;

  // ---------- Google OAuth handlers ----------
  const saveGoogleConfig = async () => {
    if (!googleForm.client_id.trim() || !googleForm.client_secret.trim()) {
      toast.error("Client ID och Client Secret krävs");
      return;
    }
    setGoogleBusy(true);
    try {
      await api.put("/google/config", googleForm);
      toast.success("Google OAuth konfigurerad");
      const c = await api.get("/google/config");
      setGoogleCfg(c.data);
      setGoogleForm({ ...googleForm, client_secret: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setGoogleBusy(false);
    }
  };

  const connectGoogle = async (label = "") => {
    setGoogleBusy(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + "/auth/google";
      const q = `redirect_uri=${encodeURIComponent(redirectUri)}` + (label ? `&label=${encodeURIComponent(label)}` : "");
      const { data } = await api.get(`/google/auth-url?${q}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte starta anslutning");
      setGoogleBusy(false);
    }
  };

  const startAddGoogleAccount = () => {
    const label = window.prompt(
      "Vad ska kontot heta i appen? (t.ex. Anton, William, Emil)\n\n" +
      "I nästa steg loggar du in med rätt Google-konto. För Värmdö-skolkonton skriver du in t.ex. anton.jonasson@edu.varmdo.se i Googles inloggning — du skickas då vidare till Värmdös portal."
    );
    if (label === null) return; // user cancelled
    connectGoogle(label.trim() || "Personlig");
  };

  const disconnectGoogle = async (tokenId = null) => {
    const msg = tokenId
      ? "Koppla bort detta Google-konto?"
      : "Koppla bort ALLA Google-konton?";
    if (!window.confirm(msg)) return;
    const q = tokenId ? `?token_id=${encodeURIComponent(tokenId)}` : "";
    await api.post(`/google/disconnect${q}`);
    toast.success("Frånkopplad");
    // Refresh status
    try {
      const { data } = await api.get("/google/status");
      setGoogleStatus(data);
      if (!data.connected) setGmailLabels([]);
    } catch (_) {
      setGoogleStatus({ connected: false, accounts: [] });
      setGmailLabels([]);
    }
  };

  const setGmailLabel = async (label) => {
    try {
      await api.put("/google/gmail/label", { label });
      setGoogleStatus({ ...googleStatus, gmail_label: label });
      toast.success("Etikett uppdaterad");
    } catch (_) {
      toast.error("Kunde inte spara");
    }
  };

  // Single ctx-prop som varje sektion får — UI-only refaktor, ingen logik flyttad.
  const sectionCtx = {
    // Familj
    users, setUsers, currentUser, setNewMemberOpen,
    editUserExtraEmails, editUserPhone, toggleChildRole,
    // Notiser
    pushEnabled, pushStatus, pushBusy, togglePush,
    runTestPush, reRegisterPush, runPushDiagnostics,
    diagBusy, diagSteps, setDiagSteps,
    // Skolkonton — Värmdö
    varmdoCreds, varmdoBusy, expandedCredId, expandedCourseId,
    credCourses, courseItems,
    toggleExpandCred, toggleExpandCourse,
    openEditVarmdo, openCreateVarmdo, syncVarmdo, syncVarmdoGmail, removeVarmdo,
    setAttachmentPreview,
    // Skolkonton — Vklass
    vklassAccounts, vklassBusy,
    openEditVklass, openCreateVklass, syncVklassAccount, removeVklassAccount,
    // Kalendrar
    feeds, students, schedulerStatus,
    briefingBusy, setBriefingBusy, briefingPreview, setBriefingPreview,
    openCreate, openEdit, refresh, remove,
    openStudentDialog, removeStudentCredentials,
  };

  // All dialog-state samlat — passas till SettingsDialogs som en single prop.
  const dlg = {
    studentOpen, setStudentOpen, studentForm, setStudentForm, studentBusy, saveStudentCredentials,
    vklassOpen, setVklassOpen, editingVklass, setEditingVklass, vklassForm, setVklassForm,
    vklassBusy, connectVklass,
    varmdoOpen, setVarmdoOpen, editingVarmdo, setEditingVarmdo, varmdoForm, setVarmdoForm,
    varmdoBusy, connectVarmdo,
    open, setOpen, editing, form, setForm, save,
    attachmentPreview, setAttachmentPreview,
    newMemberOpen, setNewMemberOpen, newMember, setNewMember, creatingMember, createNewMember,
  };

  return (
    <div className="px-5 pt-3" data-testid="settings-page">
      <button
        onClick={() => (activeSection ? setActiveSection(null) : nav("/"))}
        className="flex items-center gap-1 text-sm text-[color:var(--text-secondary)] mb-4"
        data-testid="back-button"
      >
        <ArrowLeft size={16} /> {activeSection ? "Inställningar" : "Tillbaka"}
      </button>

      <div className="mb-6">
        <p className="label-eyebrow">Inställningar</p>
        <h1 className="font-display text-2xl font-medium mt-0.5" data-testid="settings-title">
          {activeSection === "familj"
            ? "Familj"
            : activeSection === "notiser"
            ? "Notiser & enheter"
            : activeSection === "skolkonton"
            ? "Skolkonton"
            : activeSection === "kalendrar"
            ? "Kalendrar & briefing"
            : activeSection === "studie"
            ? "Studie-påminnelser"
            : activeSection === "fotboll"
            ? "Fotbollslag"
            : activeSection === "flyg"
            ? "Flyg"
            : "Familjen"}
        </h1>
      </div>

      {/* Profile */}
      <div className="card-soft p-4 mb-6">
        <p className="label-eyebrow mb-2">Inloggad som</p>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: currentUser?.avatar_color }}
          >
            {currentUser?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium">{currentUser?.name}</p>
            <p className="text-xs text-[color:var(--text-tertiary)]">{currentUser?.email}</p>
          </div>
        </div>
      </div>

      {/* Menu — visas när ingen sektion är vald */}
      {activeSection === null && (
        <div className="mb-6" data-testid="settings-menu">
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: "familj", label: "Familj", icon: Users, desc: "Medlemmar, mejl & mobil" },
              { key: "notiser", label: "Notiser & enheter", icon: Bell, desc: "Push, tysta timmar, enheter" },
              { key: "skolkonton", label: "Skolkonton", icon: GraduationCap, desc: "Classroom & Vklass" },
              { key: "kalendrar", label: "Kalendrar & briefing", icon: Calendar, desc: "iCal-feeds + söndagsmail" },
              { key: "studie", label: "Studie-påminnelser", icon: BookOpen, desc: "Morgon, eftermiddag & prov-countdown" },
              { key: "betyg", label: "Betyg", icon: Award, desc: "Vklass-betyg & meritvärde", route: "/betyg" },
              { key: "amneskatalog", label: "Ämneskatalog", icon: Library, desc: "Skolämnen som används överallt", route: "/installningar/amneskatalog" },
              { key: "fotboll", label: "Fotbollslag", icon: Trophy, desc: "Bevaka lag · Min Fotboll-live" },
              { key: "flyg", label: "Flyg", icon: Plane, desc: "Swedavia · bevakade flighter & nycklar" },
              { key: "laddaren", label: "Laddaren", icon: Zap, desc: "DEFA Power · live-status & förbrukning", route: "/laddaren" },
              { key: "meddelanden", label: "Meddelanden", icon: BellRing, desc: "Alla pushar du fått · 30 dagar", route: "/meddelanden" },
              { key: "classroom-lankar", label: "Classroom-länkar", icon: GraduationCap, desc: "Koppla nytt material till prov/läxa", route: "/installningar/classroom-lankar" },
            ].map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.key}
                  onClick={() => (m.route ? nav(m.route) : setActiveSection(m.key))}
                  className="card-soft p-4 text-left hover:border-[color:var(--moss)]/40 transition-colors"
                  data-testid={`menu-${m.key}`}
                >
                  <Icon size={20} className="text-[color:var(--moss)] mb-2" />
                  <p className="text-sm font-medium leading-snug">{m.label}</p>
                  <p className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5 leading-tight">{m.desc}</p>
                </button>
              );
            })}
            <button
              onClick={() => nav("/health")}
              className="card-soft p-4 text-left hover:border-[color:var(--jarvis)]/40 transition-colors col-span-2"
              data-testid="menu-health"
            >
              <Activity size={20} className="text-[color:var(--jarvis)] mb-2" />
              <p className="text-sm font-medium leading-snug">Hälsa & test</p>
              <p className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5 leading-tight">
                Backend-status, feed-syncar, test-push & test-mail.
              </p>
            </button>
          </div>
          <JarvisBrainCard />
        </div>
      )}

      {/* Family members */}
      {activeSection === "familj" && <FamilySection ctx={sectionCtx} />}

      {/* Notifications */}
      {activeSection === "notiser" && <NotificationsSection ctx={sectionCtx} />}

      {/* Värmdö Classroom + Vklass */}
      {activeSection === "skolkonton" && <SchoolAccountsSection ctx={sectionCtx} />}

      {/* Studie-påminnelser */}
      {activeSection === "studie" && (
        <StudyRemindersSection users={users} />
      )}

      {/* iCal-kalendrar + söndagsbriefing */}
      {activeSection === "kalendrar" && <CalendarsSection ctx={sectionCtx} />}

      {/* Bevakade fotbollslag (Min Fotboll) */}
      {activeSection === "fotboll" && <FootballTeamsSection />}

      {/* Swedavia flygintegration */}
      {activeSection === "flyg" && <FlightsSection />}

      {/* Tvinga uppdatering — gummiklocka mot envis PWA-cache.
          Avregistrerar service worker + rensar caches + reload. */}
      <ForceUpdateButton />

      <SettingsDialogs dlg={dlg} />
    </div>
  );
}

function ForceUpdateButton() {
  const [busy, setBusy] = useState(false);
  const handleForce = async () => {
    if (!window.confirm(
      "Tvinga uppdatering?\n\n"
      + "Detta avregistrerar service workern, rensar alla cacher och laddar "
      + "om appen. Använd om du ser gammal data trots ny deploy."
    )) return;
    setBusy(true);
    try {
      // 1. Unregister alla service workers
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          await reg.unregister();
        }
      }
      // 2. Töm alla cacheStorage-cacher
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 3. Töm localStorage utom auth-token (annars måste man logga in igen)
      const token = localStorage.getItem("grindstugatan_token");
      const user = localStorage.getItem("grindstugatan_user");
      localStorage.clear();
      if (token) localStorage.setItem("grindstugatan_token", token);
      if (user) localStorage.setItem("grindstugatan_user", user);
      toast.success("Cache rensad — laddar om...");
      setTimeout(() => {
        // Force reload utan cache (bypass even http cache headers)
        window.location.reload(true);
      }, 500);
    } catch (e) {
      toast.error("Kunde inte rensa cache: " + (e?.message || "okänt fel"));
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 pt-6 border-t border-[color:var(--border-subtle)]">
      <Button
        variant="outline"
        size="sm"
        onClick={handleForce}
        disabled={busy}
        className="w-full text-xs"
        data-testid="force-update-button"
      >
        🔄 Tvinga uppdatering (rensa PWA-cache)
      </Button>
      <p className="text-[10px] text-[color:var(--text-tertiary)] mt-1 text-center">
        Använd om du ser gammal data trots ny deploy
      </p>
    </div>
  );
}
